import { DayPicker, useDayPicker, type DayPickerProps } from 'react-day-picker';
import type { CalendarMonth } from 'react-day-picker';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type CalendarProps = DayPickerProps;

const MONTH_LONG_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'long' }),
);

const MONTH_SHORT_NAMES = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString('en-US', { month: 'short' }),
);

const MONTH_ITEMS = MONTH_SHORT_NAMES.map((name, index) => ({ label: name, value: index }));

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE_START = CURRENT_YEAR - 10;
const YEAR_RANGE_END = CURRENT_YEAR + 1;
const YEARS = Array.from(
  { length: YEAR_RANGE_END - YEAR_RANGE_START + 1 },
  (_, i) => YEAR_RANGE_START + i,
);

const YEAR_ITEMS = YEARS.map((year) => ({ label: String(year), value: year }));

function Caption({ calendarMonth }: { calendarMonth: CalendarMonth }) {
  const { goToMonth, previousMonth, nextMonth } = useDayPicker();

  const date = calendarMonth.date;
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();

  function handleMonthChange(value: number | null) {
    if (value === null) return;
    goToMonth(new Date(currentYear, value, 1));
  }

  function handleYearChange(value: number | null) {
    if (value === null) return;
    goToMonth(new Date(value, currentMonth, 1));
  }

  return (
    <div className="flex items-center justify-between h-7 px-1 gap-1">
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        disabled={!previousMonth}
        className="inline-flex items-center justify-center size-7 rounded-md border border-input bg-transparent hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none shrink-0"
      >
        <ChevronLeft className="size-4" />
      </button>
      <div className="flex items-center gap-1">
        <Select<number> items={MONTH_ITEMS} value={currentMonth} onValueChange={handleMonthChange}>
          <SelectTrigger size="sm" className="h-7 px-2 text-sm font-medium gap-1">
            <SelectValue />
            <SelectIcon />
          </SelectTrigger>
          <SelectContent>
            {MONTH_ITEMS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {MONTH_LONG_NAMES[item.value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select<number> items={YEAR_ITEMS} value={currentYear} onValueChange={handleYearChange}>
          <SelectTrigger size="sm" className="h-7 px-2 text-sm font-medium gap-1">
            <SelectValue />
            <SelectIcon />
          </SelectTrigger>
          <SelectContent>
            {YEAR_ITEMS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        disabled={!nextMonth}
        className="inline-flex items-center justify-center size-7 rounded-md border border-input bg-transparent hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none shrink-0"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

export function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-4',
        month: 'flex flex-col gap-4',
        month_caption: 'hidden',
        nav: 'hidden',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] text-center',
        week: 'flex w-full mt-2',
        day: 'relative p-0 text-center text-sm',
        day_button: cn(
          'inline-flex items-center justify-center size-9 rounded-md text-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        ),
        selected:
          '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground',
        today: '[&>button]:bg-accent [&>button]:text-accent-foreground',
        outside: '[&>button]:text-muted-foreground [&>button]:opacity-50',
        disabled:
          '[&>button]:text-muted-foreground [&>button]:opacity-50 [&>button]:pointer-events-none',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        MonthCaption: Caption,
        ...components,
      }}
      {...props}
    />
  );
}
